import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, FileSearch, Brain, Target, Check } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";

const STEP_ICONS = { profile: Building2, documents: FileSearch, diagnostic: Brain, plan: Target };

// Read-only journey stepper. Reflects EXISTING states only (GET endpoints already
// used elsewhere). No writes, no AI calls, no new business logic. Each step simply
// navigates to its existing module.
export function JourneyStepper() {
  const { t } = useLang();
  const { demo } = useApp();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (demo) return;
    let alive = true;
    Promise.allSettled([
      api.get("/company-profile"),
      api.get("/documents"),
      api.get("/diagnostic"),
      api.get("/action-plan"),
    ]).then((res) => {
      if (!alive) return;
      const val = (r) => (r.status === "fulfilled" ? r.value.data : null);
      const prof = val(res[0]);
      const docs = val(res[1]);
      const diag = val(res[2]);
      const plan = val(res[3]);
      setState({
        completion: prof?.completion ?? 0,
        docsCount: Array.isArray(docs) ? docs.length : 0,
        hasDiag: !!(diag && diag.diagnostic_id),
        hasPlan: !!(plan && plan.plan_id),
      });
    });
    return () => { alive = false; };
  }, [demo, pathname]);

  if (demo) return null;

  const steps = [
    { id: "profile", label: t("step_profile"), route: "/app/profile-360" },
    { id: "documents", label: t("step_documents"), route: "/app/documents" },
    { id: "diagnostic", label: t("step_diagnostic"), route: "/app/diagnostic" },
    { id: "plan", label: t("step_plan"), route: "/app/plan" },
  ];

  const statusOf = (id) => {
    if (!state) return "todo";
    if (id === "profile") return state.completion >= 100 ? "done" : state.completion > 0 ? "inprogress" : "todo";
    if (id === "documents") return state.docsCount > 0 ? "done" : "todo";
    if (id === "diagnostic") return state.hasDiag ? "done" : "todo";
    if (id === "plan") return state.hasPlan ? "done" : "todo";
    return "todo";
  };
  const isCurrent = (route) => pathname.startsWith(route);
  const statusLabel = { done: t("step_done"), inprogress: t("step_inprogress"), todo: t("step_todo") };
  const doneCount = steps.filter((s) => statusOf(s.id) === "done").length;

  return (
    <div className="lb-card p-3 sm:p-4 mb-4 sm:mb-6" data-testid="journey-stepper">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="min-w-0">
          <span className="text-xs font-semibold text-neon">{t("journey_optional_title")}</span>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-tight mt-0.5 hidden sm:block">{t("journey_optional_sub")}</p>
        </div>
        <span className="text-xs text-neon font-semibold shrink-0 ml-2" data-testid="journey-progress">{doneCount}/4</span>
      </div>
      <div className="flex items-start">
        {steps.map((s, i) => {
          const st = statusOf(s.id);
          const Icon = STEP_ICONS[s.id];
          const cur = isCurrent(s.route);
          return (
            <React.Fragment key={s.id}>
              <button type="button" onClick={() => nav(s.route)} data-testid={`journey-step-${s.id}`}
                aria-current={cur} title={`${s.label} · ${statusLabel[st]}`}
                className="flex-1 min-w-0 flex flex-col items-center gap-1.5 min-h-[44px] group">
                <span className={`relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full border transition-colors shrink-0 group-hover:brightness-110
                  ${st === "done"
                    ? "bg-gradient-to-br from-primary to-neon border-transparent text-white"
                    : cur
                    ? "border-neon/60 text-neon bg-neon/10"
                    : st === "inprogress"
                    ? "border-neon/40 text-neon bg-white/5"
                    : "border-white/15 text-[hsl(var(--muted-foreground))] bg-white/5"}`}>
                  {st === "done" ? <Check size={16} /> : <Icon size={16} />}
                  {cur && <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-neon" />}
                </span>
                <span className={`text-[11px] sm:text-xs text-center leading-tight truncate max-w-full px-0.5 ${cur ? "text-white font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}>{s.label}</span>
                <span className={`hidden sm:block text-[10px] leading-none ${st === "done" ? "text-emerald-400" : st === "inprogress" ? "text-neon" : "text-[hsl(var(--muted-foreground))]"}`}>{statusLabel[st]}</span>
              </button>
              {i < steps.length - 1 && (
                <div className="flex-none mt-4 sm:mt-[18px] w-3 sm:w-10" aria-hidden="true">
                  <span className={`block h-0.5 rounded-full ${st === "done" ? "bg-gradient-to-r from-primary to-neon" : "bg-white/10"}`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
